import React, { useState, useEffect, useRef } from 'react';
import { message, Modal, Button, Typography, Spin, FloatButton } from 'antd';
import {
    ThunderboltOutlined,
    FileImageOutlined,
    DeleteOutlined,
    LoadingOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
import html2canvas from 'html2canvas';
import AuthService from '../../services/authService';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import { useQuotaStatus } from '../../hooks/useQuotaStatus';
import SearchBar from './sections/SearchBar';
import SummaryCards from './sections/SummaryCards';
import KeywordPrecisionSection from './sections/KeywordPrecisionSection';
import DiagnosticSection from './sections/DiagnosticSection';
import RankTrendChart from './sections/RankTrendChart';
import MarketTrendSection from './sections/MarketTrendSection';
import KeywordAnalysisCard from './sections/KeywordAnalysisCard';
import KeywordTablesSection from './sections/KeywordTablesSection';
import CompetitorTagsCard from './sections/CompetitorTagsCard';
import CompetitorRankingCard from './sections/CompetitorRankingCard';
import ReviewAnalysisSection from './sections/ReviewAnalysisSection';
import PricePositioningSection from './sections/PricePositioningSection';
import IndexAnalysisSection from './sections/IndexAnalysisSection';
import SamplePreview from './sections/SamplePreview';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import './ProductAnalysis.css';

const LS_URL = 'pa_last_url';
const LS_KW = 'pa_last_keyword';

const STEPS = [
    { pct: 8, label: '요청 전송 중...' },
    { pct: 22, label: '상품 순위 조회 중...' },
    { pct: 45, label: '상품 · 경쟁사 데이터 수집 중...' },
    { pct: 63, label: '경쟁사 태그 · 리뷰 분석 중...' },
    { pct: 78, label: '키워드 데이터 수집 중...' },
    { pct: 90, label: 'AI 분석 리포트 생성 중...' },
    { pct: 97, label: '마무리 중...' },
];
const STEP_INTERVAL_MS = 4200;
const IMG_PADDING = 48;

const ProductAnalysis = () => {
    const { quota } = useQuotaStatus('productAnalysis');
    const [url, setUrl] = useState('');
    const [keyword, setKeyword] = useState('');
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [reportKeyword, setReportKeyword] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const aiLoadingRef = useRef(false);
    const [tagsState, setTagsState] = useState({ loading: false, data: null, error: false });
    const [imgSaving, setImgSaving] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyOffset, setHistoryOffset] = useState(0);
    const [historyHasMore, setHistoryHasMore] = useState(false);
    const [metricsSettled, setMetricsSettled] = useState(false);
    const [kwProgress, setKwProgress] = useState({ done: 0, total: 0 });
    const [tagsTotal, setTagsTotal] = useState(0);

    const [progress, setProgress] = useState(0);
    const [progressLabel, setProgressLabel] = useState('');
    const [elapsed, setElapsed] = useState(0);
    const stepRef = useRef(0);
    const timerRef = useRef(null);
    const elapsedRef = useRef(null);
    const startTimeRef = useRef(null);
    const contentRef = useRef(null);
    const tagsJobRef = useRef(0);
    const activeTagsJobIdRef = useRef(null);

    const startProgress = () => {
        clearInterval(timerRef.current);
        clearInterval(elapsedRef.current);
        stepRef.current = 0;
        setProgress(STEPS[0].pct);
        setProgressLabel(STEPS[0].label);
        timerRef.current = setInterval(() => {
            stepRef.current += 1;
            if (stepRef.current < STEPS.length) {
                const s = STEPS[stepRef.current];
                setProgress(s.pct);
                setProgressLabel(s.label);
            } else {
                clearInterval(timerRef.current);
            }
        }, STEP_INTERVAL_MS);

        const TOTAL = 30;
        startTimeRef.current = TOTAL;
        setElapsed(TOTAL);
        elapsedRef.current = setInterval(() => {
            startTimeRef.current -= 1;
            setElapsed(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);
    };

    const stopProgress = success => {
        clearInterval(timerRef.current);
        clearInterval(elapsedRef.current);
        if (success) {
            setProgress(100);
            setProgressLabel('분석 완료!');
            setTimeout(() => {
                setProgress(0);
                setProgressLabel('');
                setLoading(false);
            }, 800);
        } else {
            setProgress(0);
            setProgressLabel('');
            setLoading(false);
        }
    };

    useEffect(
        () => () => {
            clearInterval(timerRef.current);
            clearInterval(elapsedRef.current);
        },
        []
    );

    // 🚀 백그라운드 수집이 완료된 시점에 호출되어 정확도가 100% 보장되는 AI 전문가 분석 API
    const fetchAiExpertAnalysis = async currentReport => {
        if (aiLoadingRef.current) return;
        aiLoadingRef.current = true;
        setAiLoading(true);

        try {
            console.log(
                '📡 [AI-EXPERT] 수집 완료된 실시간 순위 데이터 기반으로 AI 전문가 분석 요청 시작'
            );
            const res = await AuthService.authAxios.post('/product/analysis/ai-expert', {
                report: currentReport,
                keyword: reportKeyword || keyword,
            });

            if (res.data?.success && res.data.expertAnalysis) {
                setReport(prev => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        expertAnalysis: res.data.expertAnalysis,
                    };
                });
            }
        } catch (err) {
            console.error('❌ Failed to load AI expert analysis:', err);
        } finally {
            setAiLoading(false);
            aiLoadingRef.current = false;
        }
    };

    // 키워드 메트릭 수집 완료(성공/실패 무관) 시 AI 분석 트리거
    // metricsSettled가 true가 되는 시점에 최신 report로 단 1회 실행
    useEffect(() => {
        if (!metricsSettled || !report || loading) return;
        if (report.expertAnalysis || aiLoadingRef.current) return;
        fetchAiExpertAnalysis(report);
    }, [metricsSettled, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchTop10Tags = async competitors => {
        // 이전 잡이 있으면 서버에 취소 요청 (fire-and-forget)
        if (activeTagsJobIdRef.current) {
            AuthService.authAxios
                .post('/product/analysis/top10-tags/cancel', { job_id: activeTagsJobIdRef.current })
                .catch(() => {});
            activeTagsJobIdRef.current = null;
        }

        const myJob = ++tagsJobRef.current;

        const smartUrls = (competitors || [])
            .map(c => c.mallUrl)
            .filter(u => typeof u === 'string' && u.includes('smartstore.naver.com'));
        if (smartUrls.length === 0) return;

        setTagsTotal(smartUrls.length);
        setTagsState({ loading: true, data: {}, error: false });

        try {
            // 1. 잡 제출
            const submitRes = await AuthService.authAxios.post('/product/analysis/top10-tags', {
                urls: smartUrls,
            });
            if (tagsJobRef.current !== myJob) return;
            if (!submitRes.data.success) {
                setTagsState({ loading: false, data: null, error: true });
                return;
            }

            const { job_id } = submitRes.data;
            activeTagsJobIdRef.current = job_id;
            let offset = 0;
            let completed = false;
            const startTime = Date.now();
            const MAX_WAIT = 120000;

            // 2. 완료되는 순서대로 500ms 간격 폴링
            while (!completed) {
                if (tagsJobRef.current !== myJob) return;
                if (Date.now() - startTime > MAX_WAIT) {
                    setTagsState(prev => ({ ...prev, loading: false, error: true }));
                    break;
                }

                await new Promise(r => setTimeout(r, 500));
                if (tagsJobRef.current !== myJob) return;

                try {
                    const streamRes = await AuthService.authAxios.get(
                        '/product/analysis/top10-tags/stream',
                        {
                            params: { job_id, offset },
                        }
                    );
                    const { status, items } = streamRes.data;

                    if (items && items.length > 0) {
                        offset += items.length;
                        if (tagsJobRef.current !== myJob) return;
                        setTagsState(prev => {
                            const newData = { ...(prev.data || {}) };
                            items.forEach(item => {
                                if (item.success) {
                                    newData[item.url] = {
                                        tags: item.tags || [],
                                        todayVisit: item.todayVisit ?? null,
                                        totalVisit: item.totalVisit ?? null,
                                    };
                                }
                            });
                            return { ...prev, data: newData };
                        });
                    }

                    if (status === 'completed' || status === 'cancelled') {
                        completed = true;
                        if (tagsJobRef.current !== myJob) return;
                        setTagsState(prev => ({ ...prev, loading: false }));
                    }
                } catch {
                    // 일시적 오류는 무시하고 계속 폴링
                }
            }
        } catch {
            setTagsState({ loading: false, data: null, error: true });
        }
    };

    const fetchKeywordMetrics = async reportData => {
        const productMid = reportData?.product?.id;
        if (!productMid || productMid === 'N/A') return;

        // 모든 대상 키워드 추출
        const recKws = (reportData.keywords || []).map(k => k.word);
        const relKws = (reportData.relatedQueries || []).map(k => k.word);
        const autKws = (reportData.autocompleteKeywords || []).map(k => k.word);
        const allKws = [...new Set([...recKws, ...relKws, ...autKws])];

        if (allKws.length === 0) {
            setMetricsSettled(true);
            return;
        }

        setKwProgress({ done: 0, total: allKws.length });

        // 🚀 실시간 점진적 로딩 (Chunking): 3개 키워드씩 끊어서 가벼운 비동기 요청 분할 전송
        const chunkSize = 3;
        const chunks = [];
        for (let i = 0; i < allKws.length; i += chunkSize) {
            chunks.push(allKws.slice(i, i + chunkSize));
        }

        // 응답이 먼저 오는 순서대로 화면의 각 키워드 지표가 실시간으로 부드럽게 채워집니다.
        // Promise.allSettled: 일부 청크 실패해도 나머지 완료 후 AI 분석을 반드시 트리거
        await Promise.allSettled(
            chunks.map(async chunk => {
                try {
                    const res = await AuthService.authAxios.post(
                        '/product/analysis/keywords/metrics',
                        {
                            keywords: chunk,
                            productMid,
                        }
                    );

                    if (res.data?.success && res.data.results) {
                        const metrics = res.data.results;
                        setReport(prev => {
                            if (!prev) return null;
                            const updateList = list =>
                                (list || []).map(item => {
                                    const m = metrics[item.word];
                                    return m ? { ...item, search: m.search, rank: m.rank } : item;
                                });

                            const nextKeywords = updateList(prev.keywords);
                            const nextRelated = updateList(prev.relatedQueries);
                            const nextAutocomplete = updateList(prev.autocompleteKeywords);

                            // 🚀 키워드 경쟁력 및 종합 점수 실시간 재계산
                            const topKwCount = nextKeywords.filter(
                                k => k.rank > 0 && k.rank <= 10
                            ).length;
                            const rankedKwCount = nextKeywords.filter(
                                k => k.rank > 0 && k.rank <= 30
                            ).length;
                            const totalKwCount = nextKeywords.length;
                            const rankRatio = totalKwCount > 0 ? rankedKwCount / totalKwCount : 0;

                            const compIdxScore =
                                prev.diagnostic?.kwCompetitiveness?.compIdxScore ?? 40;
                            const intensityScore =
                                prev.diagnostic?.kwCompetitiveness?.intensityScore ?? 80;

                            const newKwCompetitiveness = Math.round(
                                compIdxScore * 0.4 + rankRatio * 100 * 0.3 + intensityScore * 0.3
                            );

                            // 종합 점수 재계산
                            const reviewHealth = prev.diagnostic?.reviewHealth?.score || 0;
                            const rankScoreVal = prev.diagnostic?.rankScore || 0;
                            const wishScoreVal = prev.diagnostic?.wishScore || 50;
                            const priceScoreVal = prev.diagnostic?.priceScore || 50;

                            const newOverallScore = Math.round(
                                reviewHealth * 0.3 +
                                    rankScoreVal * 0.3 +
                                    newKwCompetitiveness * 0.25 +
                                    wishScoreVal * 0.1 +
                                    priceScoreVal * 0.05
                            );

                            return {
                                ...prev,
                                score: newOverallScore,
                                keywords: nextKeywords,
                                relatedQueries: nextRelated,
                                autocompleteKeywords: nextAutocomplete,
                                diagnostic: {
                                    ...prev.diagnostic,
                                    score: newOverallScore,
                                    kwCompetitiveness: {
                                        ...prev.diagnostic?.kwCompetitiveness,
                                        score: newKwCompetitiveness,
                                        topKwCount,
                                        rankedKwCount,
                                        totalKwCount,
                                    },
                                },
                            };
                        });
                    }
                } catch (err) {
                    console.error(`Failed to load metrics for chunk: ${chunk}`, err);
                }
                setKwProgress(prev => ({
                    ...prev,
                    done: Math.min(prev.done + chunk.length, prev.total),
                }));
            })
        );

        // 전체 청크 완료 (성공/실패 무관) → AI 분석 트리거 신호
        setMetricsSettled(true);
    };

    const handleAnalyze = async (latestUrl, latestKeyword) => {
        const activeUrl = latestUrl || url;
        const activeKeyword = latestKeyword || keyword;
        if (!activeUrl || !activeKeyword) {
            message.warning('상품 주소와 키워드를 입력해주세요.');
            return;
        }

        // 🌐 네이버 도메인 유효성 검사 (스마트스토어, 브랜드스토어, 쇼핑, 단축 URL 등 대응)
        const isNaverUrl = /^(https?:\/\/)?([a-z0-9-]+\.)*(naver\.com|naver\.me)\//i.test(
            activeUrl.trim()
        );
        if (!isNaverUrl) {
            message.warning('올바른 네이버 쇼핑 또는 스마트스토어 상품 주소를 입력해 주세요.');
            return;
        }
        if (!quota.unlimited && !quota.loading && quota.remaining <= 0) {
            message.error('오늘 상품 분석 한도를 모두 사용하였습니다.');
            return;
        }
        setLoading(true);
        setReport(null);
        aiLoadingRef.current = false;
        setAiLoading(false);
        setMetricsSettled(false);
        setKwProgress({ done: 0, total: 0 });
        setTagsTotal(0);
        setTagsState({ loading: false, data: null, error: false });
        startProgress();
        try {
            const response = await AuthService.authAxios.post('/product/analysis', {
                url: activeUrl,
                keyword: activeKeyword,
            });
            if (response.data.success) {
                const reportData = response.data.report;
                setReport(reportData);
                setReportKeyword(activeKeyword);
                localStorage.setItem(LS_URL, activeUrl);
                localStorage.setItem(LS_KW, activeKeyword);
                stopProgress(true);
                message.success('상품 분석이 완료되었습니다.');
                fetchTop10Tags(reportData.competitors);
                dispatchQuotaUpdated('productAnalysis');
                fetchKeywordMetrics(reportData);
                AuthService.authAxios
                    .post('/log/history-update', {
                        type: 'productAnalysis',
                        inputLog: `url=${activeUrl}|keyword=${activeKeyword}|status=success`,
                    })
                    .catch(() => {});
            } else {
                stopProgress(false);
                const errData = response.data || {};
                const isInvalidProduct = errData.code === 'INVALID_PRODUCT_URL';
                const errMsg = errData.message || errData.detail || '분석에 실패했습니다.';

                if (isInvalidProduct) {
                    dispatchQuotaUpdated('productAnalysis');
                    Modal.error({
                        title: '상품 분석 실패',
                        content: (
                            <div>
                                존재하지 않거나 판매 중단된 상품입니다.
                                <div style={{ marginTop: 8, color: '#ef4444', fontWeight: 600 }}>
                                    (분석 한도가 1회 차감됩니다.)
                                </div>
                            </div>
                        ),
                        okText: '확인',
                        centered: true,
                    });
                } else {
                    Modal.error({
                        title: '상품 분석 실패',
                        content: errMsg,
                        okText: '확인',
                        centered: true,
                    });
                }
            }
        } catch (error) {
            console.error('Analysis error:', error);
            stopProgress(false);
            const errData = error.response?.data || {};
            const isInvalidProduct = errData.code === 'INVALID_PRODUCT_URL';
            const msg = errData.detail || errData.message || '서버와 통신 중 오류가 발생했습니다.';

            if (isInvalidProduct) {
                dispatchQuotaUpdated('productAnalysis');
                Modal.error({
                    title: '상품 분석 실패',
                    content: (
                        <div>
                            존재하지 않거나 판매 중단된 상품입니다.
                            <div style={{ marginTop: 8, color: '#ef4444', fontWeight: 600 }}>
                                (분석 한도가 1회 차감됩니다.)
                            </div>
                        </div>
                    ),
                    okText: '확인',
                    centered: true,
                });
            } else {
                Modal.error({
                    title: '상품 분석 실패',
                    content: msg,
                    okText: '확인',
                    centered: true,
                });
            }
        }
    };

    const fetchHistory = async (offset = 0, append = false) => {
        setHistoryLoading(true);
        try {
            const res = await AuthService.authAxios.get('/log/history', {
                params: { gubun: 'productAnalysis', offset },
            });
            if (res.data.success) {
                const items = res.data.data || [];
                setHistoryData(prev => (append ? [...prev, ...items] : items));
                setHistoryHasMore(res.data.hasMore ?? items.length === 10);
                setHistoryOffset(offset + items.length);
            }
        } catch {
            message.error('조회 기록을 불러오지 못했습니다.');
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleApplyHistory = inputStr => {
        const parts = Object.fromEntries(
            inputStr.split('|').map(p => {
                const idx = p.indexOf('=');
                return [p.slice(0, idx), p.slice(idx + 1)];
            })
        );
        if (parts.url) setUrl(parts.url);
        if (parts.keyword) setKeyword(parts.keyword);
        setShowHistoryModal(false);
    };

    const handleDeleteHistory = async id => {
        try {
            await AuthService.authAxios.post('/log/delete', { id });
            setHistoryData(prev => prev.filter(h => h.id !== id));
        } catch {
            message.error('삭제에 실패했습니다.');
        }
    };

    const handleSaveImage = async () => {
        if (!contentRef.current || imgSaving) return;
        setImgSaving(true);
        message.loading({ content: '이미지 생성 중...', key: 'img', duration: 0 });
        try {
            const el = contentRef.current;
            const raw = await html2canvas(el, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                scrollX: 0,
                scrollY: -window.scrollY,
                windowWidth: document.documentElement.clientWidth,
                windowHeight: el.scrollHeight,
                width: el.offsetWidth,
            });

            // 상하좌우 패딩 추가
            const pad = IMG_PADDING * 2;
            const out = document.createElement('canvas');
            out.width = raw.width + pad * 2;
            out.height = raw.height + pad * 2;
            const ctx = out.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, out.width, out.height);
            ctx.drawImage(raw, pad, pad);

            const link = document.createElement('a');
            const date = new Date().toISOString().slice(0, 10);
            link.download = `상품분석_${reportKeyword}_${date}.png`;
            link.href = out.toDataURL('image/png');
            link.click();

            message.success({ content: '이미지 저장 완료!', key: 'img' });
        } catch (e) {
            console.error('Image save error:', e);
            message.error({ content: '이미지 생성에 실패했습니다.', key: 'img' });
        } finally {
            setImgSaving(false);
        }
    };

    return (
        <div className="product-analysis-page">
        <div className="product-analysis-container">
            <SearchBar
                url={url}
                keyword={keyword}
                onUrlChange={setUrl}
                onKeywordChange={setKeyword}
                onAnalyze={handleAnalyze}
                onHistoryClick={() => {
                    setShowHistoryModal(true);
                    fetchHistory(0, false);
                }}
                loading={loading}
            />

            {loading && (
                <div style={{ marginBottom: 32 }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 8,
                        }}
                    >
                        <span
                            style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#4f46e5',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            <ThunderboltOutlined /> {progressLabel}
                        </span>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>{progress}%</span>
                    </div>
                    <div className="pa-progress-track">
                        <div className="pa-progress-fill" style={{ width: `${progress}%` }} />
                    </div>
                    <div
                        style={{
                            marginTop: 6,
                            fontSize: 12,
                            color: '#94a3b8',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <span>
                            약 {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}{' '}
                            남음
                        </span>
                        <span>※ 데이터 수집 및 AI 분석으로 약 1분 소요됩니다</span>
                    </div>
                </div>
            )}

            {!report && !loading && <SamplePreview />}

            {/* 하단 심화 데이터 수집 상태바 */}
            {report && !loading && (!metricsSettled || tagsState.loading) && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#1e293b',
                        color: '#f8fafc',
                        borderRadius: 12,
                        padding: '9px 18px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        zIndex: 998,
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                    }}
                >
                    <LoadingOutlined style={{ color: '#818cf8', fontSize: 13 }} />
                    <span style={{ color: '#c7d2fe' }}>마무리 데이터 수집 중</span>
                    {kwProgress.total > 0 && !metricsSettled && (
                        <>
                            <span style={{ color: '#475569' }}>|</span>
                            <span style={{ color: '#94a3b8' }}>
                                키워드 {kwProgress.done}/{kwProgress.total}
                            </span>
                        </>
                    )}
                    {tagsState.loading && tagsTotal > 0 && (
                        <>
                            <span style={{ color: '#475569' }}>|</span>
                            <span style={{ color: '#94a3b8' }}>
                                경쟁사 태그 {Object.keys(tagsState.data || {}).length}/{tagsTotal}
                            </span>
                        </>
                    )}
                </div>
            )}

            {report && !loading && (
                <>
                    <div
                        role="button"
                        tabIndex={0}
                        className="img-save-float-wrap"
                        onClick={handleSaveImage}
                        onKeyDown={e => e.key === 'Enter' && handleSaveImage()}
                        style={{
                            position: 'fixed',
                            insetInlineEnd: 24,
                            bottom: 80,
                            zIndex: 1000,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: imgSaving ? 'not-allowed' : 'pointer',
                            opacity: imgSaving ? 0.7 : 1,
                        }}
                    >
                        <div className="img-save-float-label">이미지 저장</div>
                        <div className="img-save-float-btn">
                            <FileImageOutlined style={{ fontSize: 18, color: '#fff' }} />
                        </div>
                    </div>
                    <FloatButton.BackTop
                        style={{ insetInlineEnd: 24, bottom: 136 }}
                        visibilityHeight={0}
                    />
                    <div ref={contentRef}>
                        <SummaryCards report={report} />
                        <IndexAnalysisSection
                            report={report}
                            keyword={reportKeyword}
                            metricsSettled={metricsSettled}
                        />
                        <KeywordPrecisionSection report={report} keyword={reportKeyword} />
                        <DiagnosticSection report={report} aiLoading={aiLoading} />
                        <RankTrendChart report={report} />
                        <MarketTrendSection report={report} />
                        <KeywordAnalysisCard report={report} keyword={reportKeyword} />
                        <KeywordTablesSection report={report} />
                        <CompetitorTagsCard
                            report={report}
                            tagsState={tagsState}
                            onRetryTags={() => fetchTop10Tags(report.competitors)}
                        />
                        <CompetitorRankingCard
                            report={report}
                            tagsState={tagsState}
                            onRetryTags={() => fetchTop10Tags(report.competitors)}
                        />
                        <PricePositioningSection report={report} />
                        <ReviewAnalysisSection report={report} />
                        <div style={{ height: 60 }} />
                    </div>
                </>
            )}
            <Modal
                title="최근 조회 기록"
                open={showHistoryModal}
                onCancel={() => setShowHistoryModal(false)}
                footer={<Button onClick={() => setShowHistoryModal(false)}>닫기</Button>}
                width={680}
                styles={{ body: { maxHeight: 520, overflowY: 'auto', padding: '8px 0' } }}
                mask={{ blur: true }}
            >
                <Spin spinning={historyLoading && historyData.length === 0}>
                    {historyData.length === 0 && !historyLoading && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                            조회 기록이 없습니다.
                        </div>
                    )}
                    {historyData.map(item => {
                        const parts = Object.fromEntries(
                            item.input.split('|').map(p => {
                                const idx = p.indexOf('=');
                                return [p.slice(0, idx), p.slice(idx + 1)];
                            })
                        );
                        return (
                            <div
                                key={item.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    padding: '12px 16px',
                                    borderBottom: '1px solid #f1f5f9',
                                }}
                            >
                                <div
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 3,
                                    }}
                                >
                                    <div style={{ fontSize: 12, color: '#64748b' }}>
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                color: '#94a3b8',
                                                marginRight: 6,
                                            }}
                                        >
                                            URL
                                        </span>
                                        <Text
                                            style={{
                                                fontSize: 12,
                                                wordBreak: 'break-all',
                                                color: '#334155',
                                            }}
                                        >
                                            {parts.url || '-'}
                                        </Text>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                color: '#94a3b8',
                                                marginRight: 6,
                                            }}
                                        >
                                            키워드
                                        </span>
                                        <Text strong style={{ fontSize: 13, color: '#0f172a' }}>
                                            {parts.keyword || '-'}
                                        </Text>
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 11, marginTop: 2 }}>
                                        {item.updated_at || item.searched_at}
                                    </Text>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: 6,
                                        flexShrink: 0,
                                        alignItems: 'center',
                                    }}
                                >
                                    <Button
                                        size="small"
                                        type="primary"
                                        onClick={() => handleApplyHistory(item.input)}
                                        style={{ borderRadius: 6 }}
                                    >
                                        선택
                                    </Button>
                                    <Button
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleDeleteHistory(item.id)}
                                        style={{ borderRadius: 6 }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                    {historyHasMore && (
                        <div style={{ textAlign: 'center', padding: '12px 0' }}>
                            <Button
                                loading={historyLoading}
                                onClick={() => fetchHistory(historyOffset, true)}
                            >
                                더보기
                            </Button>
                        </div>
                    )}
                </Spin>
            </Modal>
            <KakaoFloatButton />
        </div>
        </div>
    );
};

export default ProductAnalysis;
